import { isoZ, z } from 'my-zod'
import { v4 as uuid } from 'uuid'
import { enumOptionsToObject } from '../../internal/enum-helpers'

export const endTimeAvailabilityPolicyOptions = [
  'doNotAllowEndingOutsideOfAvailability',
  'allowEndingOutsideOfAvailability'
] as const
export const endTimeAvailabilityPolicies = enumOptionsToObject(endTimeAvailabilityPolicyOptions)
export type EndTimeAvailabilityPolicy = (typeof endTimeAvailabilityPolicyOptions)[number]

/**
 * This is like a calendar event. It repeats every week from startDate to endDate.
 * The dayOfWeek field determines which days of the week this event occurs.
 * Each day this event event happens, the product is available at some point.
 * When the product is available is determined by "times".
 */
export const polarisRentalProductAvailability = z.object({
  id: z
    .string()
    .uuid()
    .initFn(() => uuid()),
  productId: z
    .string()
    .uuid()
    .initFn(() => uuid()),

  /**
   *
   */
  startDate: isoZ.date().describe('start repeating every week on this date.'),
  endDate: isoZ.date().describe('stop repeating every week on this date.'),
  dayOfWeek: z
    .enum(['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'])
    .array()
    .min(1, 'Please make a selection')
    .initFn(() => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'])
    .describe('repeat on these days of the week'),

  /**
   * startTime, endTime, and endTimeRelativeDay must be interpreted differently depending on product type
   *
   * ### Daily
   *
   * startTime: all reservations starting on this day will start at startTime
   * endTime: all reservations ending on this day will end at endTime
   * endTimeRelativeDay: not applicable
   *
   * ### Hourly
   *
   * startTime: reservations on this day start at **or after** startStart
   * endTime:reservations on this day end at **or before** endTime
   * endTimeRelativeDay: not applicable
   *
   * ### TimeSlot
   *
   * startTime: the time of day the reservation start
   * endTime: the time of day the reservation ends
   * endTimeRelativeDay: the number of days later the reservation ends
   *
   */
  startTime: isoZ.time(),
  endTime: isoZ.time(),
  endTimeRelativeDay: z.number(),
  endTimeAvailabilityPolicy: z.enum(endTimeAvailabilityPolicyOptions),

  // // /**
  // //  * this is interpreted according to the product type's duration unit. If duration unit is
  // //  * hour, then this is the min duration in hours. If the duration unit is day, then this in
  // //  * min duration of a rental in days.
  // //  */
  // minDuration: z.number(),
  durationOptions: z.number().array().nullable().describe('only used by Hourly to restrict length options on rentals'),
  /**
   * This is not applicable to TimeSlot rentals.
   *
   * For Daily, this is the maximum number of days a customer can reserve a vehicle for at one time
   *
   * For Hourly, this is currently not applicable
   */
  maxDuration: z.number().int().min(1).nullable()
})

export type polarisRentalProductAvailability = z.infer<typeof polarisRentalProductAvailability>
